import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../../../core/components/confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../../../core/components/confirmDialog/confirmDialogService';
import { LoadingService } from '../../../core/services/loading.service';
import { SettingsService } from '../../../core/services/settings/settings.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-settings-form',
  templateUrl: './settings-form.component.html',
  styleUrls: ['./settings-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsFormComponent implements OnChanges {

  // Injected services
  readonly settingsService = inject(SettingsService);
  private readonly http = inject(HttpClient);
  private readonly loadingService = inject(LoadingService);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  dialogRef: DynamicDialogRef | undefined;

  @Input() settings: any;
  @Input() form: UntypedFormGroup;
  @Input() locationId: string;
  @Output() fileChanged = new EventEmitter<void>();

  uploading = false;
  public value = '';
  public fileApi = environment.fileApi;
  readonly errorMessage = signal<string | null>(null);

  // Cached converted options to avoid recreating on every CD cycle
  private optionsCache = new Map<any[], any[]>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']) {
      // Clear cache when settings change
      this.optionsCache.clear();
    }
  }

  isCategory(category: any): boolean {
    for (const setting of category?.settingDescriptions) {
      if (setting?.type === 'String' || setting?.type === 'Boolean' || setting?.type === 'Integer' ||
          setting?.type === 'File' || setting?.type === 'Select') {
        return true;
      }
    }
    return false;
  }

  reset(field: any): void {
    this.form.controls[field?.key]?.setValue(field.defaultValue);
    this.form.controls[field?.key]?.markAsDirty();
  }

  fileChangeEvent(event: any, field: any): void {
    this.loadingService.setLoading(true);
    if (this.uploading || event.target?.files?.length < 1) {
      this.loadingService.setLoading(false);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(event.target?.files[0]);
    img.onload = () => {
      if (img.width >= field?.settings?.minWidth && img.height >= field?.settings?.minHeight) {
        this.errorMessage.set(null);
        this.uploadFile(event, field);
      } else {
        this.loadingService.setLoading(false);
        this.errorMessage.set(`Die Bildgröße sollte mindestens ${field?.settings?.minWidth}x${field?.settings?.minHeight}px.`);
        return;
      }
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      this.errorMessage.set('Invalid Datei.');
      URL.revokeObjectURL(objectUrl);
      this.loadingService.setLoading(false);
      return;
    };
    img.src = objectUrl;
  }

  uploadFile(event: any, field: any): void {
    let extension = event.target?.files[0].type;
    extension = extension.replace(/(.*)\//g, '');
    const uuid = uuidv4();
    this.value = uuid;
    this.uploading = true;
    const params = new HttpParams({
      fromObject: {
        fileName: event.target?.files[0].name
      }
    });

    const settingInput = {
      key: field?.key,
      value: this.value,
      locationId: this.locationId
    };
    this.settingsService.updateSettings(
      settingInput,
      ((result: any) => {
        const ownerId = result?.data?.updateSetting?.id;
        const url = environment.fileApi + '/file/upload/?ownerId=' + ownerId;
        const formData: FormData = new FormData();
        formData.append('file', event.target?.files[0], this.value + '.' + extension);
        lastValueFrom(this.http.post(url, formData, { params }))
          .then(() => {
            this.returnUpload();
            this.uploading = false;
          })
          .catch(() => {});
      })
    );
  }

  returnUpload(): void {
    setTimeout(() => {
      this.uploading = false;
      this.loadingService.setLoading(false);
      this.fileChanged.emit();
    }, 1000);
  }

  confirmDialog(field: any): void {
    const message = 'Möchtest Du die Datei wirklich löschen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Datei löschen'
    });
    this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.deleteFile(field);
      }
    });
  }

  deleteFile(field: any): void {
    this.loadingService.setLoading(true);
    const settingInput = {
      key: field?.key,
      value: '',
      locationId: this.locationId
    };
    this.settingsService.updateSettings(
      settingInput,
      ((result: any) => {
        this.loadingService.setLoading(false);
        this.fileChanged.emit();
      })
    );
  }

  /**
   * Converts a flat array of options into the format {name: string, value: any}[], where
   * name and value are identical. Uses caching to prevent recreating the same array.
   * @param options
   */
  convertOptions(options: any[]): any[] {
    if (!options || options.length === 0) {
      return [];
    }

    // Check if we already converted this exact array reference
    if (this.optionsCache.has(options)) {
      return this.optionsCache.get(options)!;
    }

    // Convert and cache
    const converted = options.map(option => ({ name: option, id: option }));
    this.optionsCache.set(options, converted);
    return converted;
  }
}
